/**
 * SIP API Service
 * Interacts with authenticated Phoenix SIP endpoints using the cookie session.
 */
import axios from "axios";
import { API_BASE_URL } from "../apiConstants";
import { getAuthHeader } from "./authHeader";

const SIP_API_URL = `${API_BASE_URL}/sip`;

const get = (path) => axios.get(`${SIP_API_URL}${path}`, getAuthHeader());

/**
 * Get SIP credentials for the authenticated user with retry capability
 * @param {number} retryCount - Number of retries (default 1)
 * @returns {Promise<Object>} SIP credentials
 */
export const getSipCredentials = async (retryCount = 1) => {
  try {
    const response = await get("/credentials");
    return response.data;
  } catch (error) {
    // If we have retries left and this is a network error or 5xx server error, retry
    if (
      retryCount > 0 &&
      (!error.response || (error.response && error.response.status >= 500))
    ) {
      if (import.meta.env.DEV)
        console.log(
          `Retrying SIP credentials fetch (${retryCount} retries left)...`,
        );
      // Wait before retrying with exponential backoff
      await new Promise((resolve) =>
        setTimeout(resolve, 1000 * (2 - retryCount)),
      );
      return getSipCredentials(retryCount - 1);
    }

    // Enhance error with more details for debugging
    const enhancedError = new Error(
      error.message || "Failed to get SIP credentials",
    );
    enhancedError.status = error.response?.status;
    enhancedError.originalError = error;
    enhancedError.details = {
      url: "/credentials",
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
    };

    throw enhancedError;
  }
};

/**
 * Get WebRTC configuration with retry capability
 * @param {number} retryCount - Number of retries (default 1)
 * @returns {Promise<Object>} WebRTC configuration
 */
export const getWebRtcConfig = async (retryCount = 1) => {
  try {
    const response = await get("/webrtc-config");

    if (!Array.isArray(response.data.iceServers)) {
      response.data.iceServers = [];
    }

    return response.data;
  } catch (error) {
    // If we have retries left and this is a network error or 5xx server error, retry
    if (
      retryCount > 0 &&
      (!error.response || (error.response && error.response.status >= 500))
    ) {
      if (import.meta.env.DEV)
        console.log(
          `Retrying WebRTC config fetch (${retryCount} retries left)...`,
        );
      // Wait before retrying with exponential backoff
      await new Promise((resolve) =>
        setTimeout(resolve, 1000 * (2 - retryCount)),
      );
      return getWebRtcConfig(retryCount - 1);
    }

    const enhancedError = new Error(
      error.message || "Failed to get WebRTC configuration",
    );
    enhancedError.status = error.response?.status;
    enhancedError.originalError = error;
    throw enhancedError;
  }
};

/**
 * Load the complete authenticated SIP registration bootstrap.
 * Credentials and websocket capabilities are both backend-owned.
 */
export const getSipBootstrap = async () => {
  const [credentialsPayload, webRtcPayload] = await Promise.all([
    getSipCredentials(),
    getWebRtcConfig(),
  ]);
  const credentials = credentialsPayload?.data;
  const capabilities = webRtcPayload?.data || webRtcPayload;
  const wsUri = capabilities?.wsUrl || capabilities?.websocketUrl;

  const requiredRegistrationValues = [
    credentials?.username,
    credentials?.password,
    credentials?.domain,
    wsUri,
  ];
  if (
    credentials?.ready !== true ||
    !requiredRegistrationValues.every(
      (value) => typeof value === "string" && value.trim().length > 0,
    )
  ) {
    throw new Error("SIP registration is not configured for this account");
  }

  let parsedWsUri;
  try {
    parsedWsUri = new URL(wsUri);
  } catch {
    throw new Error("SIP websocket configuration is invalid");
  }
  if (!["ws:", "wss:"].includes(parsedWsUri.protocol)) {
    throw new Error("SIP websocket configuration is invalid");
  }

  return {
    username: credentials.username,
    password: credentials.password,
    domain: credentials.domain,
    wsUri: parsedWsUri.href,
    iceServers: Array.isArray(capabilities.iceServers)
      ? capabilities.iceServers
      : [],
  };
};

/**
 * Test the WebRTC connection to diagnose connectivity issues
 * @returns {Promise<Object>} Connection test results
 */
export const testWebRtcConnection = async () => {
  try {
    // First get the WebRTC configuration
    const config = await getWebRtcConfig();

    // Create RTCPeerConnection to test ICE connectivity
    const pc = new RTCPeerConnection({ iceServers: config.iceServers });
    const results = {
      success: false,
      iceServers: config.iceServers,
      iceCandidates: [],
      errors: [],
    };

    // Set up data channel (required to generate ICE candidates)
    const dc = pc.createDataChannel("connectivity-test");

    // Set event handlers
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        results.iceCandidates.push({
          type: event.candidate.type,
          protocol: event.candidate.protocol,
          address: event.candidate.address,
          port: event.candidate.port,
        });
      }
    };

    // Add timeouts for various states
    const timeout = setTimeout(() => {
      results.errors.push("ICE gathering timed out after 5 seconds");
      pc.close();
    }, 5000);

    // Create offer and set local description
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    // Wait for ICE gathering to complete
    await new Promise((resolve) => {
      pc.onicegatheringstatechange = () => {
        if (pc.iceGatheringState === "complete") {
          clearTimeout(timeout);
          resolve();
        }
      };
    });

    // Analyze results
    const hasSrflx = results.iceCandidates.some((c) => c.type === "srflx");
    const hasRelay = results.iceCandidates.some((c) => c.type === "relay");

    results.success = hasSrflx || hasRelay; // Success if we got either STUN or TURN candidates
    results.hasStunCandidate = hasSrflx;
    results.hasTurnCandidate = hasRelay;

    // Clean up
    pc.close();

    return results;
  } catch (error) {
    return {
      success: false,
      errors: [error.message || "Unknown error during WebRTC test"],
    };
  }
};

export default {
  getSipCredentials,
  getSipBootstrap,
  getWebRtcConfig,
  testWebRtcConnection,
};
